import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import FileUpload from "@/components/FileUpload";
import { toast } from "sonner";
import { Trash2, PlusCircle, ShieldCheck, ShieldAlert } from "lucide-react";

export default function FacilityProfile() {
    const [fac, setFac] = useState(null);
    const [locations, setLocations] = useState([]);
    const [loc, setLoc] = useState({ label: "", address: "", city: "", province: "", region: "" });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const { data } = await api.get("/facilities/me");
        setFac(data.data.facility);
        setLocations(data.data.locations || []);
    };
    useEffect(() => { load(); }, []);

    if (!fac) return <div>Caricamento…</div>;
    const update = (k, v) => setFac((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/facilities/me", {
                name: fac.name, facility_type: fac.facility_type, vat_number: fac.vat_number,
                phone: fac.phone, website: fac.website, description: fac.description,
                services: fac.services, professions_needed: fac.professions_needed,
            });
            toast.success("Struttura aggiornata");
        } finally { setBusy(false); }
    };
    const addLoc = async (e) => {
        e.preventDefault();
        try {
            await api.post("/facilities/me/locations", loc);
            setLoc({ label: "", address: "", city: "", province: "", region: "" });
            load();
            toast.success("Sede aggiunta");
        } catch { toast.error("Errore"); }
    };
    const delLoc = async (id) => { await api.delete(`/facilities/me/locations/${id}`); load(); };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="font-display text-3xl font-bold tracking-tight">La tua struttura</h1>
                {fac.verification_status === "VERIFIED" ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 gap-1" data-testid="verification-badge">
                        <ShieldCheck className="w-3.5 h-3.5" />Verificata
                    </Badge>
                ) : (
                    <Badge variant="outline" className="gap-1" data-testid="verification-badge">
                        <ShieldAlert className="w-3.5 h-3.5" />
                        {fac.verification_status === "UNDER_REVIEW" ? "In verifica" : "Non verificata"}
                    </Badge>
                )}
            </div>

            <div className="hl-card p-6 space-y-4">
                <h2 className="font-display font-bold text-lg">Logo struttura</h2>
                <FileUpload kind="logo" preview={fac.logo_url} onUploaded={load} label="Carica logo" testId="upload-logo" />
            </div>

            <div className="hl-card p-6 space-y-4">
                <h2 className="font-display font-bold text-lg">Dati struttura</h2>
                <div className="grid md:grid-cols-2 gap-4">
                    <div><Label>Denominazione</Label>
                        <Input value={fac.name || ""} onChange={(e) => update("name", e.target.value)} data-testid="ff-name" /></div>
                    <div><Label>Tipologia</Label>
                        <Input value={fac.facility_type || ""} onChange={(e) => update("facility_type", e.target.value)}
                            placeholder="es. Studio dentistico, Poliambulatorio" data-testid="ff-type" /></div>
                    <div><Label>Partita IVA</Label>
                        <Input value={fac.vat_number || ""} onChange={(e) => update("vat_number", e.target.value)} data-testid="ff-vat" /></div>
                    <div><Label>Telefono</Label>
                        <Input value={fac.phone || ""} onChange={(e) => update("phone", e.target.value)} data-testid="ff-phone" /></div>
                    <div className="md:col-span-2"><Label>Sito web</Label>
                        <Input value={fac.website || ""} onChange={(e) => update("website", e.target.value)} data-testid="ff-website" /></div>
                    <div className="md:col-span-2"><Label>Descrizione</Label>
                        <Textarea rows={4} value={fac.description || ""} onChange={(e) => update("description", e.target.value)} data-testid="ff-desc" /></div>
                    <div className="md:col-span-2"><Label>Servizi (virgola)</Label>
                        <Input value={(fac.services || []).join(", ")}
                            onChange={(e) => update("services", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                            data-testid="ff-services" /></div>
                </div>
                <div className="flex justify-end"><Button onClick={save} disabled={busy} className="bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid="save-facility-btn">Salva</Button></div>
            </div>

            <div className="hl-card p-6">
                <h2 className="font-display font-bold text-lg">Sedi</h2>
                <p className="text-sm text-[#5C5C5C] mt-1 mb-4">Aggiungi le sedi in cui pubblicare turni.</p>
                <div className="space-y-2 mb-4">
                    {locations.map((l) => (
                        <div key={l.id} className="flex items-center justify-between p-3 border border-[#E5E5E0] rounded-md" data-testid={`loc-${l.id}`}>
                            <div>
                                <div className="font-semibold">{l.label}</div>
                                <div className="text-sm text-[#5C5C5C]">{l.address}, {l.city}</div>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => delLoc(l.id)} data-testid={`del-loc-${l.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                    ))}
                </div>
                <form onSubmit={addLoc} className="grid md:grid-cols-2 gap-3 border-t border-[#E5E5E0] pt-4">
                    <div><Label>Nome sede</Label><Input required value={loc.label} onChange={(e) => setLoc({ ...loc, label: e.target.value })} data-testid="loc-label" /></div>
                    <div><Label>Indirizzo</Label><Input required value={loc.address} onChange={(e) => setLoc({ ...loc, address: e.target.value })} data-testid="loc-address" /></div>
                    <div><Label>Città</Label><Input required value={loc.city} onChange={(e) => setLoc({ ...loc, city: e.target.value })} data-testid="loc-city" /></div>
                    <div><Label>Provincia</Label><Input value={loc.province} onChange={(e) => setLoc({ ...loc, province: e.target.value })} data-testid="loc-province" /></div>
                    <div className="md:col-span-2 flex justify-end">
                        <Button type="submit" className="bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid="add-loc-btn"><PlusCircle className="w-4 h-4 mr-2" />Aggiungi sede</Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
